// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../../config/api';

/**
 * BasicInfoStep Component (Step 2 of 4)
 * 
 * Basic criteria information:
 * - Name, Team (dropdown), Description
 * - Active/Default status
 */
const BasicInfoStep = ({
  formData,
  errors,
  onChange
}) => {
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);

  // Fetch teams for filtering (only user's teams)
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.TEAMS_FOR_FILTERING);
        setTeams(response.data.teams || []);
      } catch (error) {
        console.error('Failed to fetch teams for filtering:', error);
      } finally {
        setLoadingTeams(false);
      }
    };
    
    fetchTeams();
  }, []);

  const handleChange = (field, value) => {
    onChange(field, value);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 2: Basic Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter the basic information for your QA criteria
      </Typography>

      <Card>
        <CardContent>
          <Stack spacing={3}>
            {/* Name */}
            <TextField
              label="Criteria Name *"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name || 'Unique name for this criteria (e.g., "Sales Team QA v2")'}
              placeholder="e.g., Club Service Team"
            />

            {/* Team Dropdown */}
            <FormControl 
              fullWidth 
              required
              error={!!errors.team_id}
            >
              <InputLabel>Team</InputLabel>
              <Select
                value={formData.team_id || ''}
                onChange={(e) => handleChange('team_id', e.target.value)}
                label="Team"
                disabled={loadingTeams}
              >
                {loadingTeams ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 2 }} />
                    Loading teams...
                  </MenuItem>
                ) : teams.length === 0 ? (
                  <MenuItem disabled>No teams available</MenuItem>
                ) : (
                  teams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.team_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {errors.team_id}
                </Typography>
              )}
              {!errors.team_id && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 2 }}>
                  Select the team that will use this criteria
                </Typography>
              )}
            </FormControl>

            {/* Description */}
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              multiline
              rows={4}
              helperText="Brief description of this criteria's purpose and scope"
              placeholder="Describe what this QA criteria is used for..."
            />

            {/* Status Switches */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Status Options
              </Typography>
              <Stack direction="row" spacing={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => handleChange('is_active', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Active</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Criteria can be used for voice analysis
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_default}
                      onChange={(e) => handleChange('is_default', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Set as Default</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use this criteria by default for this team
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            </Box>

            {/* Info Alert */}
            <Alert severity="info" icon={<Info />}>
              <Typography variant="body2">
                <strong>Tip:</strong> Choose a descriptive name that reflects the team and purpose.
                You can always change these details later.
              </Typography>
            </Alert>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BasicInfoStep;
